import { useEffect, useRef, useState } from "react"

import { useSignIn } from "@clerk/nextjs/legacy"
import type { SignInSecondFactor } from "@clerk/shared/types"
import { Button, Input, Label } from "@coval/ui"

import { logger } from "@/lib/logger"

import {
  extractClerkError,
  shouldReportClerkAuthError,
} from "../utils/signInErrors"
import { ClerkSecondFactorFallback } from "./ClerkSecondFactorFallback"
import { SigninError } from "./ClerkSigninFactorForms"

const log = logger("ClerkSecondFactorStep")

type CodeFactor = Exclude<SignInSecondFactor, { strategy: "email_link" }>

function isCodeFactor(factor: SignInSecondFactor): factor is CodeFactor {
  return (
    factor.strategy === "totp" ||
    factor.strategy === "backup_code" ||
    factor.strategy === "phone_code" ||
    factor.strategy === "email_code"
  )
}

function factorKey(factor: SignInSecondFactor) {
  return `${factor.strategy}:${"phoneNumberId" in factor ? factor.phoneNumberId : "emailAddressId" in factor ? factor.emailAddressId : ""}`
}

function methodLabel(factor: SignInSecondFactor) {
  switch (factor.strategy) {
    case "totp":
      return "authenticator app"
    case "backup_code":
      return "a backup code"
    case "phone_code":
      return `text message to ${factor.safeIdentifier}`
    case "email_code":
      return `email to ${factor.safeIdentifier}`
    case "email_link":
      return `email link to ${factor.safeIdentifier}`
  }
  return "another verification method"
}

interface Props {
  factors: SignInSecondFactor[]
  onStartOver: () => void
  onCompleteSession: (
    sessionId: string,
    isCurrent: () => boolean,
  ) => Promise<void>
  redirectUrlComplete: string
  signUpHref: string
}

export function ClerkSecondFactorStep({
  factors,
  onCompleteSession,
  onStartOver,
  redirectUrlComplete,
  signUpHref,
}: Props) {
  const { signIn } = useSignIn()
  const [factor, setFactor] = useState(
    () =>
      factors.find((f) => f.strategy === "totp") ??
      factors.find((f) => f.strategy === "phone_code") ??
      factors[0],
  )
  const [nativeFlow] = useState(
    () =>
      typeof window !== "undefined" && window.location.hash.startsWith("#/"),
  )
  const [code, setCode] = useState("")
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState<"send" | "verify" | null>(null)
  const [error, setError] = useState<string | null>(null)
  const request = useRef(0)
  const pending = useRef(false)
  const [completedSession, setCompletedSession] = useState<string | null>(null)
  useEffect(
    () => () => {
      request.current += 1
    },
    [],
  )

  async function run(action: "send" | "verify", selected: CodeFactor) {
    if (!signIn || pending.current || (action === "send" && completedSession)) {
      return
    }
    if (
      action === "verify" &&
      (!code.trim() ||
        ((selected.strategy === "phone_code" ||
          selected.strategy === "email_code") &&
          !sent))
    ) {
      return
    }
    pending.current = true
    const generation = ++request.current
    setBusy(action)
    setError(null)
    let sessionId = completedSession
    try {
      if (action === "send") {
        if (selected.strategy === "phone_code") {
          await signIn.prepareSecondFactor({
            strategy: "phone_code",
            phoneNumberId: selected.phoneNumberId,
          })
        } else if (selected.strategy === "email_code") {
          await signIn.prepareSecondFactor({
            strategy: "email_code",
            emailAddressId: selected.emailAddressId,
          })
        } else {
          return
        }
        if (request.current === generation) {
          setSent(true)
        }
      } else {
        if (!sessionId) {
          const attempt = await signIn.attemptSecondFactor({
            strategy: selected.strategy,
            code: code.trim(),
          })
          if (request.current !== generation) {
            return
          }
          if (attempt.status !== "complete" || !attempt.createdSessionId) {
            setError("That code didn't complete sign in. Try again.")
            return
          }
          sessionId = attempt.createdSessionId
          setCompletedSession(sessionId)
        }
        await onCompleteSession(sessionId, () => request.current === generation)
      }
    } catch (err) {
      if (request.current !== generation) {
        return
      }
      if (shouldReportClerkAuthError(err)) {
        log.error("second factor failed", err)
      }
      setError(
        extractClerkError(
          err,
          action === "send"
            ? "Couldn't send the code. Try again."
            : sessionId
              ? "Couldn't finish signing in. Try again."
              : "Invalid verification code.",
        ),
      )
    } finally {
      if (request.current === generation) {
        pending.current = false
        setBusy(null)
      }
    }
  }

  if (
    nativeFlow ||
    factors.some((alternative) => !isCodeFactor(alternative)) ||
    !factor ||
    !isCodeFactor(factor)
  ) {
    return (
      <ClerkSecondFactorFallback
        status={signIn?.status}
        redirectUrlComplete={redirectUrlComplete}
        signUpHref={signUpHref}
      />
    )
  }

  const delivery =
    factor.strategy === "phone_code" || factor.strategy === "email_code"
  const inputLabel =
    factor.strategy === "totp"
      ? "Authenticator code"
      : factor.strategy === "backup_code"
        ? "Backup code"
        : "Verification code"
  const description = delivery
    ? `Send a code to ${factor.safeIdentifier}, then enter it below.`
    : factor.strategy === "totp"
      ? "Enter the 6-digit code from your authenticator app."
      : "Enter a backup code."

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-xl font-medium text-coval-text text-center md:text-left">
          Two-step verification
        </h1>
        <p className="text-sm text-coval-text-muted">{description}</p>
      </div>
      <form
        aria-label="Two-step verification"
        className="space-y-6"
        aria-describedby={error ? "signin-error" : undefined}
        onSubmit={(event) => {
          event.preventDefault()
          void run("verify", factor)
        }}
      >
        {delivery && (
          <div className="space-y-2">
            <Button
              type="button"
              variant="secondary"
              disabled={!!busy || !!completedSession}
              onClick={() => {
                void run("send", factor)
              }}
            >
              {busy === "send"
                ? "Sending…"
                : sent
                  ? "Resend code"
                  : "Send code"}
            </Button>
            <p role="status" className="text-sm text-coval-text-muted">
              {busy === "send"
                ? "Sending code…"
                : sent
                  ? `Code sent to ${factor.safeIdentifier}.`
                  : ""}
            </p>
          </div>
        )}
        <div className="space-y-1">
          <Label htmlFor="signin-mfa-code" className="font-medium">
            {inputLabel}
          </Label>
          <Input
            id="signin-mfa-code"
            name="code"
            inputMode={factor.strategy === "backup_code" ? "text" : "numeric"}
            autoComplete="one-time-code"
            value={code}
            disabled={!!busy || !!completedSession}
            required
            aria-label={inputLabel}
            data-testid="signin-mfa-input"
            onChange={(event) => setCode(event.target.value)}
          />
        </div>
        <SigninError error={error} />
        <Button
          type="submit"
          className="w-full"
          disabled={!!busy || !code.trim() || (delivery && !sent)}
          aria-label="Verify"
          data-testid="signin-mfa-submit"
        >
          {busy === "verify" ? "Verifying…" : "Verify"}
        </Button>
        <Button
          type="button"
          variant="link"
          disabled={!!busy || !!completedSession}
          onClick={onStartOver}
        >
          Use another account
        </Button>
        <div className="space-y-2">
          {factors
            .filter((alternative) => alternative !== factor)
            .map((alternative) => (
              <Button
                key={factorKey(alternative)}
                type="button"
                variant="link"
                className="block h-auto whitespace-normal text-left"
                disabled={!!busy || !!completedSession}
                data-testid="signin-mfa-toggle"
                onClick={() => {
                  if (pending.current) {
                    return
                  }
                  setCode("")
                  setSent(false)
                  setError(null)
                  setFactor(alternative)
                }}
              >
                Use {methodLabel(alternative)} instead
              </Button>
            ))}
        </div>
      </form>
    </div>
  )
}
